import { useTranslation } from 'react-i18next';
import { Box, Button, Typography, Paper, CircularProgress, Alert, Stack } from '@mui/material';

export const sx13 = { fontSize: '13px', lineHeight: 1.43 };
export const sx16 = { fontSize: '14px', lineHeight: 1.43 };

export const paperSx = {
    ...sx13,
    '& .MuiInputBase-input': { fontSize: '13px' },
    '& .MuiSelect-select': { fontSize: '13px' },
    '& .MuiAutocomplete-input': { fontSize: '13px' },
    '& .MuiChip-label': { fontSize: '13px' },
};

export function FormRow({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <Box display="flex" gap={2} alignItems="flex-start">
            <Box width={200} flexShrink={0}>
                <Typography sx={{ ...sx13 }} fontWeight="bold">{label}</Typography>
            </Box>
            <Box flex={1}>{children}</Box>
        </Box>
    );
}

interface DetailHeaderProps {
    title: React.ReactNode;
    isEditing: boolean;
    isNew: boolean;
    saving: boolean;
    canSave: boolean;
    onEdit: () => void;
    onSave: () => void;
    onCancel: () => void;
    onDelete?: () => void;
}

export function DetailHeader(props: DetailHeaderProps) {
    const { title, isEditing, isNew, saving, canSave, onEdit, onSave, onCancel, onDelete } = props;
    const { t } = useTranslation();

    return (
        <Box px={2} py={1} display="flex" alignItems="center" gap={1} bgcolor="grey.100">
            {!isEditing && (
                <Button variant="outlined" color="inherit" size="medium" onClick={onEdit}>
                    {t('common.edit')}
                </Button>
            )}
            {isEditing && (
                <>
                    <Button variant="contained" color="secondary" size="medium" onClick={onSave} disabled={!canSave || saving}>
                        {t('common.save')}
                    </Button>
                    {!isNew && (
                        <Button variant="outlined" color="inherit" size="medium" onClick={onCancel}>
                            {t('common.cancel')}
                        </Button>
                    )}
                </>
            )}
            {!isEditing && !isNew && onDelete && (
                <Button variant="outlined" color="error" size="medium" onClick={onDelete} disabled={saving}>
                    {t('common.delete')}
                </Button>
            )}
            <Typography variant="h6" sx={sx16} fontWeight="bold">{title}</Typography>
        </Box>
    );
}

interface DetailViewShellProps<T> {
    title: React.ReactNode;
    isEditing: boolean;
    isNew: boolean;
    saving: boolean;
    canSave: boolean;
    loading: boolean;
    error: string | null;
    item: T | null;
    onEdit: () => void;
    onSave: () => void;
    onCancel: () => void;
    onDelete?: () => void;
    onClearError?: () => void;
    noItemMessage?: string;
    children: (item: T) => React.ReactNode;
}

export function DetailViewShell<T>(props: DetailViewShellProps<T>) {
    if (props.loading) {
        return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
    }
    if (props.error && !props.item) {
        return <Alert severity="error">{props.error}</Alert>;
    }
    if (!props.item) {
        return props.noItemMessage ? <Alert severity="info">{props.noItemMessage}</Alert> : null;
    }

    return (
        <Paper variant="outlined" sx={paperSx}>
            <DetailHeader
                title={props.title}
                isEditing={props.isEditing}
                isNew={props.isNew}
                saving={props.saving}
                canSave={props.canSave}
                onEdit={props.onEdit}
                onSave={props.onSave}
                onCancel={props.onCancel}
                onDelete={props.onDelete}
            />
            <Box p={2}>
                <Stack spacing={3}>
                    {props.error && (
                        <Alert severity="error" onClose={props.onClearError}>
                            {props.error}
                        </Alert>
                    )}
                    {props.children(props.item)}
                </Stack>
            </Box>
            {props.saving && (
                <Box display="flex" justifyContent="center" p={1}>
                    <CircularProgress size={20} />
                </Box>
            )}
        </Paper>
    );
}

// Merge an existing array with a reference list, adding a default entry for
// any reference id that isn't already present. Used for localizations (per
// language) and country VAT rates (per global VAT).
export function mergeByKey<T, R extends { id: number }>(
    existing: T[],
    refs: R[],
    getKey: (item: T) => number,
    buildDefault: (ref: R) => T
): T[] {
    const byKey = new Map<number, T>();
    existing.forEach(item => byKey.set(getKey(item), item));
    refs.forEach(ref => {
        if (!byKey.has(ref.id)) byKey.set(ref.id, buildDefault(ref));
    });
    return Array.from(byKey.values());
}

export function mergeLocalizations<L extends { language: { id: number } }, Lang extends { id: number }>(
    existing: L[],
    languages: Lang[],
    buildDefault: (lang: Lang) => L
): L[] {
    return mergeByKey(existing, languages, l => l.language.id, buildDefault);
}
